// JobTitleTabLogic.js
import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Typography, Box, Tooltip, IconButton } from "@mui/material";
import Icon from "components/icon";
import {
  getJobTitles,
  saveJobTitle,
  deleteJobTitle,
} from "screens/Employees/logic/JobTitlelogic";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { useTheme } from "@mui/material/styles";

export const useJobTitleLogic = (toast) => {
  const navigate = useNavigate();
  const MySwal = withReactContent(Swal);
  const theme = useTheme();

  // States
  const [loading, setLoading] = useState(false);
  const [openJobTitleDlg, setOpenJobTitleDlg] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [rows, setRows] = useState([]);
  const [jobTitle, setJobTitle] = useState({
    jobtitleID: 0,
    titleName: "",
    description: "",
    isActive: true,
  });
  const [existingJobTitle, setExistingJobTitle] = useState({
    ...jobTitle,
  });
  const [jobTitleContent, setJobTitleContent] = useState({
    entity: "create",
    title: "Create New Job Title",
    btnText: "Save Job Title",
  });
  const [errors, setErrors] = useState({
    titleName: false,
  });
  const [btnDisabled, setBtnDisabled] = useState(false);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');

  //#region Handlers

  useEffect(() => {
    const isEqual =
      jobTitle &&
      existingJobTitle &&
      jobTitle.titleName === existingJobTitle.titleName &&
      jobTitle.description === existingJobTitle.description;
    setBtnDisabled(isEqual);
  }, [jobTitle, existingJobTitle]);

  useEffect(() => {
    fetchJobTitles();
  }, []);

  // Filtered rows based on search query
  const filteredRows = useMemo(() => {
    if (!searchQuery) return rows;
    
    const query = searchQuery.toLowerCase();
    return rows.filter(row => 
      row.titleName?.toLowerCase().includes(query) ||
      row.description?.toLowerCase().includes(query)
    );
  }, [rows, searchQuery]);

  // Search handler
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const fetchJobTitles = async () => {
    try {
      setLoading(true);

      const response = await getJobTitles(navigate);

      let jobTitlesData = [];

      if (response && !response.isError && response.result && Array.isArray(response.result)) {
        jobTitlesData = response.result;
      } else if (response && Array.isArray(response)) {
        jobTitlesData = response;
      } else if (response && response.data && Array.isArray(response.data)) {
        jobTitlesData = response.data;
      } else {
        jobTitlesData = [];
      }

      if (jobTitlesData.length > 0) {
        const formattedData = jobTitlesData
          .map((title) => ({
            id: title.jobtitleID || title.jobTitleID || title.id || 0,
            jobtitleID: title.jobtitleID || title.jobTitleID || 0,
            titleName: title.titleName || title.name || title.jobTitle || '',
            description: title.description || "",
            isActive: title.isActive !== undefined ? title.isActive : true,
          }))
          .sort((a, b) => a.titleName.localeCompare(b.titleName));

        setRows(formattedData);
      } else {
        setRows([]);
      }
    } catch (error) {
      toast.error("Failed to fetch job titles", {
        style: {
          fontFamily: '"DM Sans", sans-serif',
          fontSize: '15px',
        },
      });
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenJobTitleDlg = () => {
    setJobTitleContent({
      entity: "create",
      title: "Create New Job Title",
      btnText: "Save Job Title",
    });
    setJobTitle({
      jobtitleID: 0,
      titleName: "",
      description: "",
      isActive: true,
    });
    setExistingJobTitle({
      jobtitleID: 0,
      titleName: "",
      description: "",
      isActive: true,
    });
    setOpenJobTitleDlg(true);
  };

  const handleCloseJobTitleDlg = () => {
    setOpenJobTitleDlg(false);
    resetJobTitle();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setJobTitle((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    const newErrors = {
      titleName: !jobTitle.titleName?.trim(),
    };

    setErrors(newErrors);

    if (Object.values(newErrors).some(error => error)) {
      toast.error("Please enter job title name", {
        style: {
          fontFamily: '"DM Sans", sans-serif',
          fontSize: '15px',
        },
      });
      return;
    }

    try {
      setLoading(true);
      const payload = {
        jobtitleID: jobTitle.jobtitleID || 0,
        titleName: jobTitle.titleName.trim(),
        description: jobTitle.description || "",
        isActive: jobTitle.isActive !== undefined ? jobTitle.isActive : true,
      };

      const result = await saveJobTitle(payload, navigate);

      if (result && !result.isError) {
        toast.success(
          `Job Title ${
            jobTitleContent.entity === "create" ? "created" : "updated"
          } successfully`,
          {
            style: {
              fontFamily: '"DM Sans", sans-serif',
              fontSize: '15px',
            },
          }
        );
        handleCloseJobTitleDlg();
        fetchJobTitles();
        resetJobTitle();
      } else {
        toast.error(
          result?.message ||
            `Failed to ${
              jobTitleContent.entity === "create" ? "create" : "update"
            } job title`,
          {
            style: {
              fontFamily: '"DM Sans", sans-serif',
              fontSize: '15px',
            },
          }
        );
      }
    } catch (error) {
      toast.error("An error occurred while saving job title", {
        style: {
          fontFamily: '"DM Sans", sans-serif',
          fontSize: '15px',
        },
      });
    } finally {
      setLoading(false);
    }
  };

  const resetJobTitle = () => {
    setJobTitle({
      jobtitleID: 0,
      titleName: "",
      description: "",
      isActive: true,
    });
    setErrors({ titleName: false });
  };

  const showDeleteConfirmation = (id) => {
    MySwal.fire({
      title: "<strong>Are you sure?</strong>",
      html: "<span>You won't be able to revert this!</span>",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, keep it",
      width: "390px",
      padding: "1rem",
      focusConfirm: false,
      showCloseButton: true,
      closeButtonHtml: "&times;",
      customClass: {
        popup:
          theme.palette.mode === "dark"
            ? "custom-swal-popup-dark"
            : "custom-swal-popup",
        title:
          theme.palette.mode === "dark"
            ? "custom-swal-title-dark"
            : "custom-swal-title",
        htmlContainer: "custom-swal-html",
        confirmButton: "custom-swal-confirm",
        cancelButton:
          theme.palette.mode === "dark"
            ? "custom-swal-cancel-dark"
            : "custom-swal-cancel",
        closeButton: "custom-swal-close",
      },
    }).then((result) => {
      if (result.isConfirmed) {
        handleDeleteJobTitle(id);
      }
    });
  };

  const handleDeleteJobTitle = async (id) => {
    try {
      const result = await deleteJobTitle(id, navigate);

      if (result && result.isError) {
        const errorMessage = result.message || "Job title failed to delete.";
        if (
          errorMessage.toLowerCase().includes("not found") ||
          errorMessage.toLowerCase().includes("default") ||
          errorMessage.toLowerCase().includes("cannot be deleted")
        ) {
          toast.error("Default job titles cannot be deleted", {
            position: "top-right",
            autoClose: 4000,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
            style: {
              fontFamily: '"DM Sans", sans-serif',
              fontSize: '15px',
            },
          });
        } else {
          toast.error(errorMessage, {
            position: "top-right",
            autoClose: 4000,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
            style: {
              fontFamily: '"DM Sans", sans-serif',
              fontSize: '15px',
            },
          });
        }
      } else {
        fetchJobTitles();
        toast.success("Job title deleted successfully!", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          style: {
            fontFamily: '"DM Sans", sans-serif',
            fontSize: '15px',
          },
        });
      }
    } catch (error) {
      toast.error("An error occurred while deleting job title", {
        style: {
          fontFamily: '"DM Sans", sans-serif',
          fontSize: '15px',
        },
      });
    }
  };

  const handleJobTitleEdit = (id) => {
    const jobTitleToEdit = rows.find((title) => {
      return title.jobtitleID === id;
    });

    if (jobTitleToEdit) {
      setJobTitleContent({
        entity: "edit",
        title: "Edit Job Title",
        btnText: "Save Changes",
      });
      setJobTitle({
        jobtitleID: jobTitleToEdit.jobtitleID,
        titleName: jobTitleToEdit.titleName,
        description: jobTitleToEdit.description || "",
        isActive:
          jobTitleToEdit.isActive !== undefined
            ? jobTitleToEdit.isActive
            : true,
      });
      setExistingJobTitle({
        jobtitleID: jobTitleToEdit.jobtitleID,
        titleName: jobTitleToEdit.titleName,
        description: jobTitleToEdit.description || "",
        isActive:
          jobTitleToEdit.isActive !== undefined
            ? jobTitleToEdit.isActive
            : true,
      });

      setOpenJobTitleDlg(true);
    } else {
      toast.error("Job title not found", {
        style: {
          fontFamily: '"DM Sans", sans-serif',
          fontSize: '15px',
        },
      });
    }
  };

  //#region Columns Configuration
  const columns = [
    {
      flex: 0.1,
      minWidth: 150,
      field: "titleName",
      headerName: "Job Title Name",
      renderCell: (params) => {
        const { row } = params;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography noWrap variant="body1">
              {row.titleName}
            </Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      minWidth: 200,
      field: "description",
      headerName: "Description",
      renderCell: (params) => {
        const { row } = params;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography noWrap variant="body1">
              {row.description || "-"}
            </Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "actions",
      minWidth: 180,
      maxWidth: 180,
      headerName: "Action",
      renderCell: (params) => {
        const { row } = params;
        return (
          <>
            <Tooltip title="Edit">
              <IconButton
                onClick={() => handleJobTitleEdit(row.jobtitleID)}
              >
                <Icon icon="tabler:pencil" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                color="error"
                onClick={() => showDeleteConfirmation(row.jobtitleID)}
              >
                <Icon icon="tabler:trash" />
              </IconButton>
            </Tooltip>
          </>
        );
      },
    },
  ];
  //#endregion Columns Configuration

  const JobTitleProps = {
    jobTitleContent,
    resetJobTitle,
    jobTitle,
    setJobTitle,
    existingJobTitle,
    handleChange,
    errors,
    handleSave,
    rows,
    fetchJobTitles,
    columns,
    paginationModel,
    setPaginationModel,
    openJobTitleDlg,
    handleOpenJobTitleDlg,
    handleCloseJobTitleDlg,
    btnDisabled,
    loading,
    searchQuery,
    handleSearchChange,
    filteredRows,
  };

  return {
    JobTitleProps,
  };
};